import { useCallback, useEffect, useRef } from "react";
import { CloudOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PickedElement } from "@/lib/requests/element-picker";
import { summarizePick } from "@/lib/requests/element-picker";

/**
 * Presentational only — owns no submit/queue/picker state. All of it is owned
 * by the mounting parent, RequestButtonInner (franklycatering #497). The
 * "Point at something" button renders ONLY when `onStartPicking` is provided
 * AND `pick` is null.
 */
export interface RequestComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: string;
  onMessageChange: (v: string) => void;
  pick: PickedElement | null;
  onClearPick?: () => void;
  onStartPicking?: () => void;
  pendingCount: number;
  isPending: boolean;
  onSubmit: () => void;
}

function fitToContent(el: HTMLTextAreaElement) {
  el.style.height = "auto"; // scrollHeight never reports less than clientHeight
  el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`; // add borders back (border-box)
}

export function RequestComposer(props: RequestComposerProps) {
  const {
    open, onOpenChange, message, onMessageChange, pick, onClearPick,
    onStartPicking, pendingCount, isPending, onSubmit,
  } = props;
  const isMobile = useIsMobile();
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const attachComposer = useCallback((el: HTMLTextAreaElement | null) => {
    taRef.current = el;
    if (el) fitToContent(el);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (taRef.current) fitToContent(taRef.current);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const body = (
    <>
      {pick ? (
        <div
          data-testid="request-pick-chip"
          className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm"
        >
          <span className="flex-1 truncate">{summarizePick(pick)}</span>
          {onClearPick && (
            <button
              type="button"
              data-testid="request-pick-clear"
              aria-label="Clear picked element"
              onClick={onClearPick}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        onStartPicking && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="request-pick-start"
            onClick={onStartPicking}
            className="w-fit"
          >
            Point at something
          </Button>
        )
      )}

      <Textarea
        autoFocus
        ref={attachComposer}
        value={message}
        onChange={(e) => {
          onMessageChange(e.target.value);
          fitToContent(e.target);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSubmit();
        }}
        placeholder="What's wrong here?"
        rows={4}
        maxLength={4000}
        className="max-h-64 resize-none text-base md:text-base"
        data-testid="request-input"
      />

      {pendingCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CloudOff className="h-3.5 w-3.5" />
          {pendingCount} waiting to send
        </div>
      )}
    </>
  );

  const footer = (
    <Button
      onClick={onSubmit}
      disabled={isPending || !message.trim()}
      data-testid="request-save"
    >
      {isPending ? "Sending…" : "Send"}
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" data-requests className="flex flex-col gap-3">
          <SheetHeader>
            <SheetTitle>New request</SheetTitle>
            <SheetDescription className="sr-only">Describe what's wrong or what you want changed.</SheetDescription>
          </SheetHeader>
          {body}
          <SheetFooter>{footer}</SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-requests className="sm:max-w-lg flex flex-col gap-3">
        <DialogHeader>
          <DialogTitle>New request</DialogTitle>
          <DialogDescription className="sr-only">Describe what's wrong or what you want changed.</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter>{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
